// English. The reference catalogue: nl.ts must have exactly these keys.
//
// Strings that existed before translation are kept word for word, so the
// screen tests — which run in English — still describe the same screen.

import { documentsEn } from './documents.en.ts';
import { sourceEn } from './source.en.ts';
import { statesEn } from './states.en.ts';
import { messagesEn } from './messages.en.ts';
import { queueEn } from './queue.en.ts';

export type Vars = Readonly<Record<string, string | number>>;
export type Message = string | ((vars: Vars) => string);

const n = (vars: Vars, name = 'count'): number => Number(vars[name] ?? 0);

export const en = {
  // Each part's own file, so the parts can be translated independently.
  ...documentsEn,
  ...sourceEn,
  ...statesEn,
  ...messagesEn,
  ...queueEn,
  // --- shared -----------------------------------------------------------------
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.reading': 'Reading…',
  'common.moreInfo': 'More information',
  'common.viewAll': 'View all',
  'common.view': 'View',
  'common.estimate': 'Estimate',
  'common.thisMonth': 'This month',
  'common.notReadable': 'This could not be read.',
  'common.hours': (v: Vars) => (n(v) === 1 ? 'hour' : 'hours'),
  'common.days': (v: Vars) => (n(v) === 1 ? 'day' : 'days'),

  // --- the shell --------------------------------------------------------------
  'nav.today': 'Today',
  'nav.work': 'Work queue',
  'nav.administrations': 'Administrations',
  'nav.documents': 'Documents',
  'nav.bank': 'Bank matching',
  'nav.messages': 'Customer communication',
  'nav.rules': 'Working rules',
  'nav.connections': 'Connections',
  'nav.activity': 'Activity',
  'nav.ask': 'Ask Fleur',
  'nav.agency-offices': 'Offices',
  'nav.agency-onboarding': 'Onboarding',
  'nav.agency-workspace': 'Office workspace',
  'nav.agency-usage': 'Costs & usage',
  'nav.group.workspace': 'Workspace',
  'nav.group.assistant': 'Assistant',
  'nav.group.manage': 'Manage',
  'nav.group.agency': 'Agency',
  'nav.main': 'Main navigation',
  'nav.assistantNav': 'Assistant navigation',
  'nav.agencyNav': 'Agency navigation',
  'nav.waitingBadge': (v: Vars) => `${n(v)} waiting`,
  'shell.byline': 'by Agency AI',
  'shell.officeWorkspace': 'Office workspace',
  'shell.office': 'Office',
  'shell.signOut': 'Sign out',
  'shell.acrossOffices': 'Across the offices you are granted',
  'shell.selectAdministration': 'Select administration',
  'shell.allAdministrations': 'All administrations',
  'shell.language': 'Language',
  'role.owner': 'Owner',
  'role.reviewer': 'Reviewer',
  'role.viewer': 'Viewer',
  'role.agency_operator': 'Agency operator',

  'search.placeholder': 'Search documents, suppliers, amounts',
  'search.label': 'Search documents, suppliers and amounts',
  'search.results': 'Search results',
  'search.searching': 'Searching…',
  'search.unreachable': 'The service could not be reached.',
  'search.failed': 'The search could not be run.',
  'search.none': (v: Vars) => `No document matches “${v['text']}” in the administrations you can see.`,
  'search.notAmount': ' The text was not read as an amount: it has more than one reading, or none.',
  'search.alsoAmount': (v: Vars) => `Also compared as the amount ${v['amount']}.`,
  'search.noInvoiceNumber': 'No invoice number',
  'search.matchedOn': (v: Vars) => `matched on ${v['field']}`,
  'search.field.invoice_number': 'invoice number',
  'search.field.supplier': 'supplier',
  'search.field.amount': 'amount',
  'search.administration': 'Administration',

  // --- Today ------------------------------------------------------------------
  'today.greeting.morning': 'Good morning',
  'today.greeting.afternoon': 'Good afternoon',
  'today.greeting.evening': 'Good evening',
  'today.waitingLine': (v: Vars) =>
    n(v) === 0
      ? 'Nothing is waiting for your approval.'
      : `${n(v)} ${n(v) === 1 ? 'item is' : 'items are'} waiting for your approval.`,
  'today.upload': 'Upload document',
  'today.kpi.waiting': 'Waiting for approval',
  'today.kpi.waitingHint': 'Open in the work queue',
  'today.kpi.booked': 'Booked this month',
  'today.kpi.bookedHint': 'Confirmed by the accounting package',
  'today.kpi.saved': 'Time saved',
  'today.kpi.savedHint': 'How this is calculated',
  'today.kpi.vat': 'Until the VAT return',
  'today.kpi.vatHint': (v: Vars) => `${v['label']} · due ${v['due']}`,
  'today.kpi.unavailable': 'Not available',
  'today.waiting.title': 'Waiting for your approval',
  'today.waiting.empty': 'Nothing is waiting for your approval.',
  'today.recent.title': 'Just happened',
  'today.recent.all': 'All activity',
  'today.recent.empty': 'Nothing has happened yet in this selection.',
  'today.spend.title': (v: Vars) => `Spend in ${v['month']}`,
  'today.spend.sub': 'Net, confirmed bookings',
  'today.spend.exVat': 'excl. VAT',
  'today.spend.empty': 'No purchase has been booked this month yet.',
  'today.spend.other': 'Other',
  'today.spend.ledger': (v: Vars) => `Ledger ${v['code']}`,
  'today.spend.notSynced': (v: Vars) => `Ledger ref. ${v['ref']} (not synced yet)`,
  'today.spend.currencies': (v: Vars) => `${n(v)} currencies`,
  'today.approved.title': 'Proposals without changes',
  'today.approved.empty': 'No booking has been approved this month yet.',
  'today.approved.of': (v: Vars) =>
    `${v['unchanged']} of ${v['total']} ${Number(v['total']) === 1 ? 'proposal' : 'proposals'} approved as the AI prepared ${Number(v['total']) === 1 ? 'it' : 'them'}.`,
  'today.approved.changed': (v: Vars) => `${v['changed']} changed before approval`,
  'today.approved.info': 'Proposals approved exactly as your AI prepared them, without a person editing them first.',
  'today.vat.title': 'VAT return',
  'today.vat.daysLeft': (v: Vars) => `${n(v) === 1 ? 'day' : 'days'} left`,
  'today.vat.period': (v: Vars) => `${v['label']} · ${v['from']} – ${v['to']} · due ${v['due']}`,
  'today.vat.confirmed': (v: Vars) => `${v['confirmed']} of ${v['counted']} documents booked`,
  'today.vat.open': (v: Vars) => `${v['open']} still open`,
  'today.vat.noneDated': (v: Vars) => `No document is dated in ${v['label']} yet.`,
  'today.vat.undated': (v: Vars) =>
    `${n(v)} more ${n(v) === 1 ? 'is' : 'are'} not read yet and have no invoice date, so they are not counted in a period.`,
  'today.vat.details': 'VAT details',
  'today.vat.bookedLabel': 'Documents in this period, booked',
  'today.vat.openLabel': 'Documents in this period, still open',
  'today.saved.dialogTitle': 'How time saved is calculated',
  'today.saved.formula': 'Documents booked this month × your minutes by hand, minus the review time we measured.',
  'today.saved.documents': 'Documents booked this month',
  'today.saved.perDocument': 'Minutes by hand per document (your office’s figure)',
  'today.saved.manual': 'Booking by hand',
  'today.saved.measured': (v: Vars) => `Measured review (${v['measured']} of ${v['documents']} documents)`,
  'today.saved.result': 'Time saved',
  'today.saved.cap': (v: Vars) =>
    `A review is measured from opening the source to approving it, up to ${v['cap']} minutes. An unmeasured review counts as no time.`,
  'today.saved.none': 'No booking has been confirmed this month, so no time has been saved on one.',
  'today.minutes': (v: Vars) => `${v['value']} min`,
  'today.hoursShort': (v: Vars) => `${v['value']} h`,

  'today.toWork': 'Go to the work queue',
  'today.waiting.settles': 'would settle this invoice',
  'today.waiting.paymentFound': (v: Vars) => `Payment found for invoice ${v['invoice']}`,
  'today.waiting.check': 'Check',
  'today.kpi.waitingTag': 'Do now',
  'today.duration': (v: Vars) =>
    n(v, 'hours') === 0 ? `${n(v, 'minutes')} m` : n(v, 'minutes') === 0 ? `${n(v, 'hours')} h` : `${n(v, 'hours')} h ${n(v, 'minutes')} m`,
  'today.recent.live': 'Live',
  'today.vat.due': (v: Vars) => `due ${v['due']}`,
  'today.vat.progress': (v: Vars) =>
    `${v['confirmed']} of ${v['counted']} documents booked, ${v['open']} still open.`,
  'work.kind.document': 'Document',
  'work.kind.proposal': 'Booking proposal',
  'work.kind.operation': 'Booking in progress',
  'work.kind.bank_match': 'Bank match',
  'work.kind.request': 'Customer request',
  'work.docType.invoice': 'Invoice',
  'work.docType.credit_note': 'Credit note',
  'work.docType.receipt': 'Receipt',
  'work.docType.bank_statement': 'Bank statement',
  'time.justNow': 'just now',

  // --- "Just happened": what the system did, in a bookkeeper's words ----------
  'feed.extraction.completed': 'Invoice read, booking prepared',
  'feed.extraction.blocked': 'Invoice read, something needs a check',
  'feed.extraction.abandoned': 'Invoice could not be read',
  'feed.proposal.revised': 'Booking adjusted',
  'feed.proposal.approved': 'Booking approved',
  'feed.document.excluded': 'Document excluded',
  'feed.bank_match.approved': 'Bank transaction matched',
  'feed.operation.submitted': (v: Vars) => `Booking sent to ${v['package']}`,
  'feed.operation.confirmed': (v: Vars) => `Booked in ${v['package']}`,
  'feed.operation.failed_safe': 'Booking stopped, nothing was changed',
  'feed.operation.unknown': 'Not yet certain the booking arrived, checking',
  'feed.operation.unknown_after_crash': 'Not yet certain the booking arrived, checking',
  'feed.operation.duplicate_present': (v: Vars) => `Booking was already in ${v['package']}`,
  'feed.operation.needs_review': 'Booking needs a manual check',
  'feed.operation.probe_requested': 'Status requested from the accounting package',
  'feed.operation.retry_authorised': 'Sending again was allowed',
  'feed.statement.imported': 'Bank statement imported',
  'feed.statement.blocked': 'Bank statement needs a check',
  'feed.statement.abandoned': 'Bank statement could not be read',
  'feed.connection.sync_succeeded': (v: Vars) => `Synchronised with ${v['package']}`,
  'feed.connection.sync_failed': (v: Vars) => `Synchronisation with ${v['package']} failed`,
  'feed.counterparty.classified': 'Supplier classified',
  'feed.document.source_url_issued': 'Document opened',
  'feed.document.source_downloaded': 'Document opened',
  'feed.other': 'Recorded',
  'feed.package': 'the accounting package',

  // --- Documents: the booked view ---------------------------------------------
  'documents.view.open': 'Open',
  'documents.view.booked': 'Booked this month',
  'documents.booked.empty': 'Nothing has been booked this month yet.',
  'documents.booked.more': (v: Vars) => `Showing the latest ${v['shown']} of ${v['total']}.`,

  // --- Upload -----------------------------------------------------------------
  'upload.title': 'Upload document',
  'upload.drop': 'Drop a PDF, photo or MT940 here',
  'upload.kinds': "PDF, JPEG, PNG, plain text or an MT940 bank statement, onto one of this office's approved upload routes",
  'upload.noRoute': 'No upload route is approved for this office. An owner adds one under Working rules.',
  'upload.route': 'Route',
  'upload.routeLabel': 'Upload route',
  'upload.fileLabel': 'Document file',
  'upload.button': 'Upload',
  'upload.busy': 'Uploading…',
  'upload.tooLarge': (v: Vars) => `This file is ${v['size']}; the limit is ${v['limit']}.`,
  'upload.held': (v: Vars) => `Received and held back: ${v['blockers']}. Nothing will be read from it.`,
  'upload.received': 'Received. It is read in the background; its state below says how far it got.',
  'upload.failed': 'The upload failed.',
  'upload.tooLargeApi': 'The file is larger than the API accepts.',
  'upload.unreachable': 'The service could not be reached. Nothing was uploaded.',
  'upload.refused': (v: Vars) => `Refused (${v['code']})${v['fields'] === '' ? '' : ` — ${v['fields']}`}.`,

  // --- Working rules ----------------------------------------------------------
  'rules.title': 'Working rules',
  'rules.intro': 'What your office has settled, so the AI does not have to keep asking.',
  'rules.introInfo':
    'Who its counterparties are, which ledger a supplier books to, where documents arrive from, and what may ever run without a person. Every control here writes a record with a name and a time; nothing is a switch.',
  'rules.section.notAvailable': 'This is not available to your session.',
  'rules.section.roleCannotRead': 'Your role does not allow reading this.',
  'rules.section.unreachable': 'The service could not be reached.',
  'rules.action.failed': 'The request could not be completed.',
  'rules.action.stale': 'This changed since the screen was loaded, so nothing was recorded. Reload and look at what is there now.',
  'rules.action.forbidden': 'Your role does not allow this action.',
  'rules.action.gone': 'That is no longer there — somebody may have dealt with it first.',
  'rules.action.refused': (v: Vars) => `The request was refused: ${v['fields']}.`,
  'rules.action.validation': 'validation failed',

  // Booking rules (supplier → ledger)
  'rules.booking.title': 'Booking rules',
  'rules.booking.sub': 'Which ledger each supplier is booked to',
  'rules.booking.info':
    'A rule is saved when a reviewer confirms a suggested supplier on a document. Read-only here: a rule is added from the document it is needed for.',
  'rules.booking.reading': 'Reading the booking rules…',
  'rules.booking.empty': 'No supplier has a booking rule yet.',
  'rules.booking.openDocuments': 'Go to documents',
  'rules.booking.notConfigured': (v: Vars) => `${v['name']}: no booking rules are set up yet.`,
  'rules.booking.supplier': 'Supplier',
  'rules.booking.matchedOn': 'Recognised by',
  'rules.booking.ledger': 'Ledger',
  'rules.booking.vat': 'VAT',
  'rules.booking.default': (v: Vars) => `Always to ${v['ledger']} (default)`,
  'rules.booking.always': (v: Vars) => `Always to ${v['ledger']}`,
  'rules.booking.showAll': (v: Vars) => `Show all ${v['count']}`,
  'rules.booking.showLess': 'Show fewer',
  'rules.booking.search': 'Search suppliers',
  'rules.booking.noMatch': (v: Vars) => `No supplier matches “${v['text']}”.`,
  'rules.routes.more': (v: Vars) => `More for ${v['name']}`,
  'rules.routes.uploadTitle': 'Uploading in the dashboard',
  'rules.bank.title': 'Bank and cash',
  'rules.bank.info': 'How bank transactions reach the accounting package, which ledger cash receipts are paid from, and where a settled credit note is booked. Changed under Administrations, by the owner or the agency.',
  'rules.bank.reading': 'Reading the settings…',
  'rules.bank.delivery': 'Bank data',
  'rules.bank.cash': 'Cash receipts',
  'rules.bank.settlement': 'Credit note settlement',
  'rules.bank.liveFeed': (v: Vars) => `Via ${v['package']}`,
  'rules.bank.defaultLiveFeed': (v: Vars) => `Via ${v['package']} (default)`,
  'rules.bank.mt940': 'MT940 upload',
  'rules.bank.notSet': 'Not set up yet',
  'rules.routes.approvedOn': (v: Vars) => `Approved on ${v['day']}`,
  'rules.routes.active': 'Active',
  'rules.routes.mailTitle': 'Forwarding by e-mail',
  'rules.routes.mailUnset': 'No address set up yet',
  'rules.routes.setUp': 'Set up',
  'rules.auto.offTitle': 'Automatic booking is off',
  'rules.auto.offLine': 'Every booking waits for approval from you or a colleague.',
  'rules.auto.onTitle': 'Automatic booking is on',
  'rules.auto.onLine': 'What has been released is booked without approval.',
  'rules.booking.defaultUnknown': 'Default cost ledger',
  'rules.booking.vatNumber': (v: Vars) => `VAT ${v['value']}`,
  'rules.booking.kvk': (v: Vars) => `KvK ${v['value']}`,
  'rules.booking.name': (v: Vars) => `Name “${v['value']}”`,
  'rules.booking.deductible': 'Deductible',
  'rules.booking.notDeductible': 'Not deductible',
  'rules.booking.oneOff': 'One-off party',
  'rules.booking.unnamedRelation': (v: Vars) => `Relation ${v['id']}`,
  'rules.booking.notSynced': (v: Vars) => `Ledger ${v['id']} (not synced yet)`,

  // Counterparties
  'rules.cp.title': 'Suppliers to check',
  'rules.cp.sub': 'Parties we could not tell apart on paper',
  'rules.cp.info': 'Parties we could not tell apart on the paper alone, and what this office has decided about them.',
  'rules.cp.reading': 'Reading the counterparty questions…',
  'rules.cp.empty': 'Everything is clear',
  'rules.cp.emptyLine': 'When the AI cannot recognise a supplier, you will see it here.',
  'rules.cp.emptyInfo':
    'Every party on a document so far carried enough to tell: a VAT number, a KvK number, an existing relation, or a second invoice from the same party.',
  'rules.cp.noName': 'A party with no readable name',
  'rules.cp.arrived': (v: Vars) => `${v['administration']} · arrived ${v['day']}`,
  'rules.cp.noNumbers': 'no VAT or KvK number on the document',
  'rules.cp.numbers': (v: Vars) => `VAT ${v['vat']} · KvK ${v['kvk']}`,
  'rules.cp.open': 'Open the document',
  'rules.cp.answer': 'Answer',
  'rules.cp.answered': (v: Vars) =>
    `Answered as ${v['kind'] === 'one_off' ? 'a one-off purchase' : 'a recurring supplier'}. Waiting for the document to be read again — nothing more to do.`,
  'rules.cp.answeredBadge': 'Answered',
  'rules.cp.decidedTitle': 'What has been decided',
  'rules.cp.decidedNone': 'Nothing has been classified in this office yet.',
  'rules.cp.decidedInfo':
    'Older answers are kept rather than replaced. Documents were booked on them, and “what did we think this party was, and who said so?” has to stay answerable for each of those.',
  'rules.cp.col.party': 'Party',
  'rules.cp.col.matchedOn': 'Matched on',
  'rules.cp.col.decided': 'Decided',
  'rules.cp.col.by': 'By',
  'rules.cp.col.when': 'When',
  'rules.cp.vatNumber': 'VAT number',
  'rules.cp.name': 'Name',
  'rules.cp.oneOff': 'One-off',
  'rules.cp.recurring': 'Recurring supplier',
  'rules.cp.superseded': 'superseded',
  'rules.cp.recordedOneOff':
    'Recorded as a one-off purchase. Nothing permanent was created, and the document is being read again.',
  'rules.cp.recordedRecurring':
    'Recorded as a recurring supplier. Which relation it books against is still a mapping somebody has to approve, so the document will wait for that.',

  // Classify control (also on the document)
  'classify.question': 'Is this a one-off purchase, or a supplier you buy from regularly?',
  'classify.why':
    'Nothing on this document says which — no VAT number, no KvK number, and we have not seen this party before. Your answer is recorded as a decision, with your name and the time.',
  'classify.oneOff': 'One-off purchase',
  'classify.oneOffDetail': '— booked against this administration’s one-off counterparty. No permanent relation is created.',
  'classify.notSure': 'Choose this if you are not sure.',
  'classify.recurring': 'Recurring supplier',
  'classify.recurringDetail':
    '— this party should have its own relation. Which relation that is still has to be approved, so the document will wait for that.',
  'classify.record': 'Record this decision',

  // Intake routes
  'rules.routes.title': 'Where documents come in',
  'rules.routes.sub': 'Each address belongs to one administration',
  'rules.routes.info': 'Each address belongs to one administration, and the office approves the mapping.',
  'rules.routes.add': 'Add a route',
  'rules.routes.roleCannot': (v: Vars) => `Your role (${v['role']}) may not approve a route.`,
  'rules.routes.administration': 'Administration',
  'rules.routes.channel': 'Channel',
  'rules.routes.address': 'Address',
  'rules.routes.addressLabel': 'Source address',
  'rules.routes.secret': 'Signing secret reference',
  'rules.routes.secretHint': 'The name the secret is stored under — never the secret itself.',
  'rules.routes.approve': 'Approve this route',
  'rules.routes.reading': 'Reading the intake routes…',
  'rules.routes.empty': 'No route is configured yet.',
  'rules.routes.emptyInfo':
    'Nothing has an address to arrive at. A document sent anywhere goes to the routing queue instead of into an administration, which is the safe answer and not a useful one.',
  'rules.routes.approved': (v: Vars) => `${v['channel']} · ${v['administration']} · approved ${v['day']}`,
  'rules.routes.secretOk': ' · signing secret configured',
  'rules.routes.secretMissing': ' · no signing secret, so deliveries are refused',
  'rules.routes.revoke': 'Revoke',
  'rules.routes.revoked': (v: Vars) =>
    `${n(v)} revoked route${n(v) === 1 ? ' is' : 's are'} kept, so which rule routed a document stays answerable.`,
  'rules.routes.created': 'The route is approved. Documents arriving at that address now belong to that administration.',
  'rules.routes.revokedNotice':
    'The route is revoked and its address is free again. Anything that still arrives there goes to the routing queue.',
  'rules.channel.mailbox': 'Mailbox',
  'rules.channel.mailboxDetail': 'An address we publish for one administration. Documents emailed to it arrive here.',
  'rules.channel.upload': 'Upload',
  'rules.channel.uploadDetail': 'An inbox a signed-in reviewer uploads to.',
  'rules.channel.webhook': 'Webhook',
  'rules.channel.webhookDetail': 'A provider posts to a signed endpoint. Needs a signing secret in the secret store.',

  // Triage
  'rules.triage.title': 'Arrivals nobody could route',
  'rules.triage.sub': 'Documents sent to an address with no route',
  'rules.triage.reading': 'Reading the routing queue…',
  'rules.triage.notGranted': 'This queue is not yours to work.',
  'rules.triage.notGrantedInfo':
    'An arrival that could not be routed belongs to no office yet, so working the queue needs a separate grant that is given to named operators rather than held by a role. Ask your agency contact; nothing is hidden from you that belongs to this office.',
  'rules.triage.empty': 'Nothing is waiting.',
  'rules.triage.emptyInfo': 'Every document that has arrived reached an address with a route behind it.',
  'rules.triage.arrived': (v: Vars) => `arrived ${v['when']}`,
  'rules.triage.fileInto': 'File into',
  'rules.triage.fileIntoLabel': (v: Vars) => `File ${v['address']} into`,
  'rules.triage.dismissReason': 'Or dismiss, with a reason',
  'rules.triage.dismissLabel': (v: Vars) => `Dismiss ${v['address']} because`,
  'rules.triage.file': 'File it',
  'rules.triage.dismiss': 'Dismiss',
  'rules.triage.readOnly': 'read only',
  'rules.triage.roleCannot': (v: Vars) => `Your role (${v['role']}) may not file an arrival.`,
  'rules.triage.foot':
    'You are deciding on the address a document was sent to, not on what is in it. The document is not shown here and cannot be: an arrival with no office has no office whose reviewers may read it. If the address is not enough, ask the customer whose it is.',
  'rules.triage.filed': 'Filed. The document is in that administration and will be read like any other arrival.',
  'rules.triage.dismissed': 'Dismissed. The arrival is kept with your reason on it and is out of the queue.',
  'rules.unrouted.no_route': 'Nothing is configured for the address it was sent to.',
  'rules.unrouted.route_revoked': 'The route for that address has been retired.',
  'rules.unrouted.ambiguous_source': 'It arrived at two addresses belonging to two different administrations.',

  // Automation
  'rules.auto.title': 'Automation',
  'rules.auto.sub': 'One scope at a time: tested, approved, released',
  'rules.auto.info': 'One scope at a time, each one tested, approved by this office and released separately.',
  'rules.auto.request': 'Ask for a scope',
  'rules.auto.lockTitle': 'Nothing here runs by itself yet.',
  'rules.auto.lockInfo':
    'A released scope is a recorded permission with evidence and an approval behind it, and no part of this system acts on one: every document and every match still waits for a person. When that changes, a release will say so here rather than this line disappearing quietly.',
  'rules.auto.scope': 'Scope',
  'rules.auto.amountLimit': 'Amount limit (EUR)',
  'rules.auto.amountLimitLabel': 'Amount limit',
  'rules.auto.record': 'Record this request',
  'rules.auto.recorded':
    'Recorded. It cannot be edited — a change is a new version — and nothing happens until it has been tested, approved and released.',
  'rules.auto.reading': 'Reading the automation policies…',
  'rules.auto.empty': 'Nothing is automated, and nothing has been asked for.',
  'rules.auto.emptyInfo':
    'Every document is prepared and waits for a person. That is where a new administration starts and where it stays until a scope has been tested and approved.',
  'rules.auto.version': (v: Vars) => `${v['scope']} · version ${v['version']}`,
  'rules.auto.askedFor': (v: Vars) => `${v['administration']} · asked for ${v['day']} · policy ${v['hash']}…`,
  'rules.auto.tested': 'Tested',
  'rules.auto.notYet': 'Not yet.',
  'rules.auto.cases': (v: Vars) => `${v['passed']} of ${v['total']} cases`,
  'rules.auto.approvedBy': 'Approved by this office',
  'rules.auto.notApproved': 'Not for the results that were last recorded.',
  'rules.auto.released': 'Released',
  'rules.auto.no': 'No.',
  'rules.auto.boundTo': (v: Vars) => `${v['day']} · bound to ${v['versions']}.`,
  'rules.auto.and': (v: Vars) => `${v['a']} and ${v['b']}`,
  'rules.auto.inForce': 'In force.',
  'rules.auto.notInForce': 'It authorises nothing yet — every item still waits for a person.',
  'rules.auto.connectorVersion': 'Connector version',
  'rules.auto.capabilityEvidence': 'Capability evidence',
  'rules.auto.capabilityEvidenceLabel': 'Capability evidence reference',
  'rules.auto.modelVersion': 'Model version (if any)',
  'rules.auto.modelVersionLabel': 'Model version',
  'rules.auto.dataset': 'Test dataset',
  'rules.auto.datasetLabel': 'Test dataset reference',
  'rules.auto.results': 'Test results',
  'rules.auto.resultsLabel': 'Test results reference',
  'rules.auto.casesRun': 'Cases run',
  'rules.auto.casesPassed': 'Cases passed',
  'rules.auto.recordEvidence': 'Record the evidence',
  'rules.auto.evidenceRecorded':
    'Recorded. The office has to approve these exact results before anything can be released on them.',
  'rules.auto.recordTestEvidence': 'Record test evidence',
  'rules.auto.approveResults': 'Approve these results',
  'rules.auto.approvedNotice': 'Approved, bound to those exact results. Re-running the tests will ask you again.',
  'rules.auto.release': 'Release',
  'rules.auto.releasedNotice': 'Released, bound to the versions that were tested. It authorises nothing on its own yet.',
  'rules.scope.standard_purchase_booking': 'Standard purchase booking',
  'rules.scope.standard_purchase_booking.detail':
    'Ordinary purchase invoices with a mapped supplier, one currency and no credit notes.',
  'rules.scope.standard_sales_booking': 'Standard sales booking',
  'rules.scope.standard_sales_booking.detail': 'Ordinary sales invoices issued from this administration.',
  'rules.scope.exact_bank_matching': 'Exact bank matching',
  'rules.scope.exact_bank_matching.detail': 'Bank lines that match one open item exactly. No partials, no residuals.',
  'rules.scope.standard_missing_document_requests': 'Standard missing-document requests',
  'rules.scope.standard_missing_document_requests.detail': 'Asking a customer for a receipt we can see is missing.',
  'rules.gate.test_evidence': 'Waiting for test evidence',
  'rules.gate.test_evidence.who': 'Your agency operator runs the tests and records the results.',
  'rules.gate.office_approval': 'Waiting for your approval',
  'rules.gate.office_approval.who': 'The office owner approves the exact results that were recorded.',
  'rules.gate.release': 'Waiting to be released',
  'rules.gate.release.who': 'Your agency operator releases it, binding the versions that were tested.',
  'rules.gate.released': 'Released',
  'rules.gate.released.who': 'Recorded and bound to the evidence and the approval it was given on.',
} satisfies Record<string, Message>;

export type MessageKey = keyof typeof en;
export type Messages = Readonly<Record<MessageKey, Message>>;
